import React from 'react';
import { Scissors, Phone, MapPin, ShieldCheck, Heart, ExternalLink, Lock, FileText, Cookie, Mail, Building2 } from 'lucide-react';
import { salonInfo } from '../data/salonInfo';
import { services } from '../data/services';
import { useLanguage } from '../context/LanguageContext';

export default function Footer({ 
  onOpenBooking, 
  onOpenAdmin, 
  onOpenPrivacy, 
  onOpenTerms, 
  onOpenCookies,
  onOpenSpecialDays,
  onNavigate
}) {
  const { lang, t } = useLanguage();

  return (
    <footer className="bg-slate-950 text-slate-300 pt-16 pb-12 border-t border-slate-800/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-slate-800/80">
          
          {/* Brand & Contact Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Scissors className="w-5 h-5 text-slate-950 stroke-[2.5]" />
              </div>
              <span className="font-serif text-xl font-bold text-white tracking-wider">
                {salonInfo.name}
              </span>
            </div>

            <p className="text-xs text-slate-400 leading-relaxed">
              {salonInfo.description[lang] || salonInfo.description.nl}
            </p>

            {/* Official Registration & Contact Details */}
            <div className="pt-2 space-y-2 text-xs text-slate-300">
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <a 
                  href={salonInfo.googleMapsUrl}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-amber-400 transition-colors"
                >
                  {salonInfo.address.full}
                </a>
              </div>
              
              <div className="flex items-center gap-2.5 text-slate-400">
                <Phone className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                <a href={`tel:${salonInfo.phoneRaw}`} className="hover:text-white transition-colors">
                  {salonInfo.phone}
                </a>
              </div>

              <div className="flex items-center gap-2.5 text-slate-400">
                <Mail className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                <a href={`mailto:${salonInfo.email}`} className="hover:text-white transition-colors">
                  {salonInfo.email}
                </a>
              </div>

              {/* Social Media Links */}
              <div className="pt-2 flex items-center gap-2">
                <a
                  href={salonInfo.socials.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  title="Instagram"
                  className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:border-pink-500/50 hover:bg-pink-600/20 transition-all"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a
                  href={salonInfo.socials.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  title="TikTok"
                  className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:border-cyan-500/50 hover:bg-cyan-600/20 transition-all"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-1.01v8.83c-.02 1.94-.58 3.89-1.71 5.48-1.22 1.74-3.09 2.94-5.18 3.32-2.12.38-4.36-.07-6.18-1.19-1.8-1.1-3.13-2.86-3.71-4.88-.58-2.02-.38-4.26.54-6.11.93-1.84 2.56-3.27 4.54-3.95.89-.31 1.83-.47 2.77-.47.16 0 .32.01.48.02v4.12c-.44-.12-.91-.18-1.37-.16-1.09.04-2.15.53-2.86 1.37-.71.84-1 1.96-.8 3.05.21 1.09.91 2.03 1.89 2.54.98.51 2.16.53 3.16.05 1-.48 1.69-1.48 1.79-2.59.04-.61.02-1.23.02-1.84V.02h2.92z"/></svg>
                </a>
                <a
                  href={salonInfo.socials.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  title="Facebook"
                  className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:border-blue-500/50 hover:bg-blue-600/20 transition-all"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a
                  href={salonInfo.socials.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                  title="YouTube"
                  className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:border-red-500/50 hover:bg-red-600/20 transition-all"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
              </div>
            </div>
          </div>

          {/* Popular Services Links */}
          <div className="space-y-3">
            <h3 className="font-serif text-sm font-bold text-white uppercase tracking-wider">
              {t.footer.popularServices}
            </h3>
            <ul className="space-y-2 text-xs">
              {services.slice(0, 5).map((s) => {
                const sName = typeof s.name === 'object' ? (s.name[lang] || s.name.nl) : s.name;
                return (
                  <li key={s.id}>
                    <button
                      onClick={() => onOpenBooking(s)}
                      className="hover:text-amber-400 transition-colors text-left cursor-pointer"
                    >
                      {sName}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Quick Navigation Links */}
          <div className="space-y-3">
            <h3 className="font-serif text-sm font-bold text-white uppercase tracking-wider">
              {t.footer.quickLinks}
            </h3>
            <ul className="space-y-2 text-xs">
              <li>
                <a 
                  href="#home" 
                  onClick={(e) => { if (onNavigate) { e.preventDefault(); onNavigate('home'); } }} 
                  className="hover:text-amber-400 transition-colors cursor-pointer"
                >
                  {lang === 'nl' ? 'Home' : lang === 'tr' ? 'Ana Sayfa' : 'Home'}
                </a>
              </li>
              <li>
                <a 
                  href="#hizmetler" 
                  onClick={(e) => { if (onNavigate) { e.preventDefault(); onNavigate('services'); } }} 
                  className="hover:text-amber-400 transition-colors cursor-pointer"
                >
                  {t.nav.services}
                </a>
              </li>
              <li>
                <a 
                  href="#haarwerk" 
                  onClick={(e) => { if (onNavigate) { e.preventDefault(); onNavigate('hairpiece'); } }} 
                  className="hover:text-amber-400 transition-colors cursor-pointer"
                >
                  {t.nav.hairpiece}
                </a>
              </li>
              <li>
                <a 
                  href="#producten" 
                  onClick={(e) => { if (onNavigate) { e.preventDefault(); onNavigate('products'); } }} 
                  className="hover:text-amber-400 transition-colors cursor-pointer"
                >
                  {t.nav.products || (lang === 'nl' ? 'Producten' : lang === 'tr' ? 'Ürünler' : 'Products')}
                </a>
              </li>
              <li>
                <a 
                  href="#galeri" 
                  onClick={(e) => { if (onNavigate) { e.preventDefault(); onNavigate('gallery'); } }} 
                  className="hover:text-amber-400 transition-colors cursor-pointer"
                >
                  {t.nav.gallery}
                </a>
              </li>
              <li>
                <a 
                  href="#yorumlar" 
                  onClick={(e) => { if (onNavigate) { e.preventDefault(); onNavigate('reviews'); } }} 
                  className="hover:text-amber-400 transition-colors cursor-pointer"
                >
                  {t.nav.reviews}
                </a>
              </li>
              <li>
                <a 
                  href="#iletisim" 
                  onClick={(e) => { if (onNavigate) { e.preventDefault(); onNavigate('contact'); } }} 
                  className="hover:text-amber-400 transition-colors cursor-pointer"
                >
                  {t.nav.contact}
                </a>
              </li>
              {onOpenSpecialDays && (
                <li className="pt-1">
                  <button
                    type="button"
                    onClick={onOpenSpecialDays}
                    className="text-amber-400 hover:text-amber-300 font-bold transition-colors cursor-pointer flex items-center gap-1.5"
                  >
                    <span>🎉 {lang === 'nl' ? '2026 Feestdagen Kortingen' : lang === 'tr' ? '2026 Özel Gün İndirimleri' : '2026 Holiday Deals'}</span>
                    <span className="px-1.5 py-0.2 rounded bg-amber-500 text-slate-950 text-[10px] font-mono font-black">%20</span>
                  </button>
                </li>
              )}
            </ul>
          </div>

          {/* Direct Actions & Booking */}
          <div className="space-y-4">
            <h3 className="font-serif text-sm font-bold text-white uppercase tracking-wider">
              {t.footer.onlineBooking}
            </h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              {t.footer.bookFast}
            </p>
            
            <button
              onClick={onOpenBooking}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500 via-amber-600 to-amber-500 text-slate-950 font-black text-xs uppercase tracking-wider transition-all shadow-md shadow-amber-500/20 hover:scale-[1.02] cursor-pointer"
            >
              {t.nav.bookNow}
            </button>

            {/* Opening Hours Summary */}
            <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-800 text-[11px] text-slate-400 space-y-1">
              <div className="text-slate-300 font-bold flex items-center justify-between">
                <span>{lang === 'nl' ? 'Dinsdag - Zaterdag:' : lang === 'tr' ? 'Salı - Cumartesi:' : 'Tuesday - Saturday:'}</span>
                <span className="text-emerald-400 font-mono">09:00 - 17:00</span>
              </div>
              <div className="flex items-center justify-between text-slate-400">
                <span>{lang === 'nl' ? 'Zondag & Maandag:' : lang === 'tr' ? 'Pazar & Pazartesi:' : 'Sunday & Monday:'}</span>
                <span>{lang === 'nl' ? 'Gesloten' : lang === 'tr' ? 'Kapalı' : 'Closed'}</span>
              </div>
            </div>
          </div>

        </div>

        {/* Legal & Compliance Footer Bar */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          
          <div className="space-y-1 text-center md:text-left">
            <p>© {new Date().getFullYear()} {salonInfo.name} Rotterdam. {t.footer.allRights}</p>
            <p className="text-[11px] text-slate-400">
              Gevestigd aan het Samuel Esmeijerplein 3, 3067 HP Rotterdam
            </p>
          </div>

          {/* Legal Links (Privacy, Algemene Voorwaarden, Cookie Settings, Admin) */}
          <div className="flex flex-wrap items-center justify-center md:justify-end gap-4 text-xs font-medium">
            <button
              type="button"
              onClick={onOpenPrivacy}
              className="hover:text-amber-400 transition-colors underline cursor-pointer flex items-center gap-1"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
              <span>{lang === 'nl' ? 'Privacyverklaring' : lang === 'tr' ? 'Gizlilik Politikası' : 'Privacy Policy'}</span>
            </button>

            <span className="text-slate-700">•</span>

            <button
              type="button"
              onClick={onOpenTerms}
              className="hover:text-amber-400 transition-colors underline cursor-pointer flex items-center gap-1"
            >
              <FileText className="w-3.5 h-3.5 text-amber-400" />
              <span>{lang === 'nl' ? 'Algemene Voorwaarden' : lang === 'tr' ? 'Kullanım Koşulları' : 'Terms of Service'}</span>
            </button>

            <span className="text-slate-700">•</span>

            <button
              type="button"
              onClick={onOpenCookies}
              className="hover:text-amber-400 transition-colors underline cursor-pointer flex items-center gap-1"
            >
              <Cookie className="w-3.5 h-3.5 text-amber-400" />
              <span>{lang === 'nl' ? 'Cookie-instellingen' : lang === 'tr' ? 'Çerez Ayarları' : 'Cookie Settings'}</span>
            </button>

            <span className="text-slate-700">•</span>

            <button
              type="button"
              onClick={onOpenAdmin}
              className="hover:text-white transition-colors text-slate-400 hover:text-slate-200 text-[11px] flex items-center gap-1 cursor-pointer"
              title={lang === 'nl' ? 'Kappers Beheer' : lang === 'tr' ? 'Salon Yönetici Girişi' : 'Admin Portal'}
            >
              <Lock className="w-3 h-3" />
              <span>Admin</span>
            </button>
          </div>

        </div>

      </div>
    </footer>
  );
}
